import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import Checkbox from "../../components/ui/Checkbox";
import Select from "../../components/ui/Select";
import SearchableSelectBox from "../../components/ui/SearchableSelectBox";
import { API_BASE_PATH } from "../../apiHelper";
import FastAPIClient from "../../services/apiClient";
import { DataTable2 } from "../../components/ui/DataTable2";
import { Plus, Edit, Trash2, Save, Loader2, Info, ChevronDown } from "lucide-react"; // Icons
import { useToast } from "../../hooks/useToast"; // Import your custom useToast hook

const client = new FastAPIClient(); // Assuming config is handled internally by client or env vars

const TransactionMappingManager = ({
    transactionMappings,
    setTransactionMappings,
    glAccounts,
    TransactionTypeEnum,
    onSaveSuccess, // Callback to refresh parent data (for maps)
    onTransactionTypeAdded, // New callback to refresh transaction types in parent
    user // User object for API calls
}) => {
    const { showToast } = useToast(); // Use your custom useToast hook

    const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
    const [isAddTransactionTypeModalOpen, setIsAddTransactionTypeModalOpen] = useState(false); // New state for new modal
    const [editingMapping, setEditingMapping] = useState(null);
    const [formState, setFormState] = useState({
        transaction_type: '',
        debit_account_id: '',
        credit_account_id: '',
        description: '',
        is_active: true,
    });
    const [newTransactionTypeForm, setNewTransactionTypeForm] = useState({
        transaction_type: '',
        label: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingTransactionType, setIsAddingTransactionType] = useState(false); // New saving state for transaction type
    const [showAddDropdown, setShowAddDropdown] = useState(false); // State for dropdown visibility
    const dropdownRef = useRef(null); // Ref for dropdown to handle clicks outside

    // Memoized options for Select components
    const transactionTypeOptions = useMemo(() => Object.values(TransactionTypeEnum).map(type => ({
        value: type,
        label: type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase())
    })), [TransactionTypeEnum]);

    const glAccountOptions = useMemo(() => glAccounts.map(acc => ({
        value: acc.gl_account_id,
        label: `${acc.account_number} - ${acc.account_name}`
    })), [glAccounts]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowAddDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Reset form state for mapping modal
    const resetMappingForm = useCallback(() => {
        setFormState({
            transaction_type: '',
            debit_account_id: '',
            credit_account_id: '',
            description: '',
            is_active: true,
        });
        setEditingMapping(null);
    }, []);

    // Reset form state for new transaction type modal
    const resetNewTransactionTypeForm = useCallback(() => {
        setNewTransactionTypeForm({
            transaction_type: '',
            label: ''
        });
    }, []);

    // Open modal for new or editing mapping
    const openMappingModal = useCallback((mapping = null) => {
        if (mapping) {
            setEditingMapping(mapping);
            setFormState({
                transaction_type: mapping.transaction_type,
                debit_account_id: mapping.debit_account_id,
                credit_account_id: mapping.credit_account_id,
                description: mapping.description || '',
                is_active: mapping.is_active,
            });
        } else {
            resetMappingForm();
        }
        setIsMappingModalOpen(true);
        setShowAddDropdown(false); // Close dropdown after selection
    }, [resetMappingForm]);

    // Close mapping modal
    const closeMappingModal = useCallback(() => {
        setIsMappingModalOpen(false);
        resetMappingForm();
    }, [resetMappingForm]);

    // Open new transaction type modal
    const openAddTransactionTypeModal = useCallback(() => {
        resetNewTransactionTypeForm();
        setIsAddTransactionTypeModalOpen(true);
        setShowAddDropdown(false); // Close dropdown after selection
    }, [resetNewTransactionTypeForm]);

    // Close new transaction type modal
    const closeAddTransactionTypeModal = useCallback(() => {
        setIsAddTransactionTypeModalOpen(false);
        resetNewTransactionTypeForm();
    }, [resetNewTransactionTypeForm]);

    // Handle form input changes for mapping modal
    const handleMappingChange = useCallback((e) => {
        const { id, value, type, checked } = e.target;
        setFormState(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : (id === 'debit_account_id' || id === 'credit_account_id') ? parseInt(value, 10) : value,
        }));
    }, []);

    // Handle form input changes for new transaction type modal
    const handleNewTransactionTypeChange = useCallback((e) => {
        const { id, value } = e.target;
        setNewTransactionTypeForm(prev => ({
            ...prev,
            [id]: value,
        }));
    }, []);

    // Get account name for display in table
    const getAccountName = useCallback((id) => {
        const account = glAccounts.find(acc => acc.gl_account_id === id);
        return account ? `${account.account_number} - ${account.account_name}` : 'N/A';
    }, [glAccounts]);

    // Save/Update mapping via API
    const saveOrUpdateMapping = async (payload) => {
        setIsSaving(true);
        try {
            let response;
            // Ensure users_id is included in the payload for both create and update
            const dataToSend = { ...payload, users_id: user.users_id };

            if (editingMapping && editingMapping.mapping_id && !String(editingMapping.mapping_id).startsWith('default_')) {
                // Update existing mapping (PUT request)
                // console.log(dataToSend)
                response = await client.postData(`${API_BASE_PATH}/accounting/create-transaction-map`, dataToSend);
                showToast(response.msg || "Mapping updated successfully!", 'success'); // Using custom showToast
            } else {
                // Create new mapping (POST request)
                // console.log('sending transaction mapping data',dataToSend)
                response = await client.postData(`${API_BASE_PATH}/accounting/create-transaction-map`, dataToSend);
                showToast(response.msg || "Mapping created successfully!", 'success'); // Using custom showToast
            }
            onSaveSuccess(); // Trigger parent to reload data (transaction maps)
            closeMappingModal();
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error saving mapping: ${err.message || err}`;
            showToast(errorMessage, 'error'); // Using custom showToast
            console.error(errorMessage, err);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle form submission (new or edit mapping)
    const handleMappingSubmit = async (e) => {
        e.preventDefault();
        await saveOrUpdateMapping(formState);
    };

    // Handle specific "Save" action for default mappings (if still needed, though now handled by create)
    const handleSaveDefaultMapping = async (mapping) => {
        // This function might become redundant if all new mappings are handled by create-transaction-map
        // For now, it's kept for existing default mapping save logic
        await saveOrUpdateMapping(mapping);
    };

    // Handle delete mapping via API
    const handleDeleteMapping = async (mappingId) => {
        if (!window.confirm('Are you sure you want to delete this mapping?')) {
            return;
        }
        setIsSaving(true);
        try {
            // Check if it's a default/unsaved mapping (should ideally not be deletable this way)
            if (String(mappingId).startsWith('default_')) {
                setTransactionMappings(prev => prev.filter(map => map.mapping_id !== mappingId));
                showToast("Default mapping removed from list locally.", 'success'); // Using custom showToast
                // No API call needed for local default mappings
            } else {
                // Actual API call for saved mappings
                const response = await client.deleteData(`${API_BASE_PATH}/accounting/delete-transaction-map/${mappingId}`);
                showToast(response.msg || "Mapping deleted successfully!", 'success'); // Using custom showToast
                onSaveSuccess(); // Trigger parent to reload data
            }
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error deleting mapping: ${err.message || err}`;
            showToast(errorMessage, 'error'); // Using custom showToast
            console.error(errorMessage, err);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle submission for adding a new transaction type
    const handleAddTransactionTypeSubmit = async (e) => {
        e.preventDefault();
        setIsAddingTransactionType(true);
        try {
            const payload = {
                transaction_type: newTransactionTypeForm.transaction_type.toUpperCase().replace(/ /g, '_'), // Ensure uppercase and underscores
                label: newTransactionTypeForm.label,
                organization_id: user.users_id,
            };
            const response = await client.postData(`${API_BASE_PATH}/accounting/add-transaction-type`, payload);
            showToast(response.msg || "Transaction type added successfully!", 'success');
            onTransactionTypeAdded(); // Callback to refresh transaction types in parent
            closeAddTransactionTypeModal();
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error adding transaction type: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setIsAddingTransactionType(false);
        }
    };

    const dataTableColumns = useMemo(() => [
        { key: 'transaction_type', label: 'Transaction Type', render: (type) => type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase()) },
        { key: 'debit_account_id', label: 'Debit Account', render: (id) => getAccountName(id) },
        { key: 'credit_account_id', label: 'Credit Account', render: (id) => getAccountName(id) },
        { key: 'description', label: 'Description', render: (desc) => desc || '-' },
        { key: 'is_active', label: 'Active', render: (isActive) => isActive ? 'Yes' : 'No' },
    ], [getAccountName]);

    return (
        <div className="p-6 bg-white rounded-lg shadow-md h-full flex flex-col">
            <div className="flex justify-between items-center mb-6 z-50">
                <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                    <Edit size={24} className="text-gmail-blue" /> Manage General Transaction Mappings
                </h2>
                <div className="relative" ref={dropdownRef}>
                    <Button
                        onClick={() => setShowAddDropdown(!showAddDropdown)}
                        variant="primary"
                        className="flex items-center gap-2 px-4 py-2"
                    >
                        <Plus size={20} /> Add New <ChevronDown size={16} />
                    </Button>
                    {showAddDropdown && (
                        <div className="absolute right-0 mt-2 w-60 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <Button
                                onClick={() => openMappingModal()}
                                variant="ghost"
                                className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-100 rounded-md"
                            >
                                <Plus size={18} /> Add New Mapping
                            </Button>
                            <Button
                                onClick={openAddTransactionTypeModal}
                                variant="ghost"
                                className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-100 rounded-md"
                            >
                                <Plus size={18} /> Add Transaction Type
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0"> {/* This div ensures DataTable can flex correctly */}
                <DataTable2
                    columns={dataTableColumns}
                    data={transactionMappings}
                    emptyMessage="No transaction mappings configured yet. Click 'Add New Mapping' to get started!"
                    enableSearch={true}
                    onEdit={(row) => openMappingModal(row)}
                    onDelete={(row) => handleDeleteMapping(row.mapping_id)}
                    rowActions={[]} // No extra dropdown actions needed as edit/delete are direct buttons
                    // Custom render for the save button on unsaved default mappings
                    renderCustomActions={(row) => (
                        <>
                            {row.canSave && (
                                <Button
                                    variant="primary"
                                    onClick={() => handleSaveDefaultMapping(row)}
                                    className="px-3 py-1 text-xs flex items-center gap-1"
                                    disabled={isSaving}
                                >
                                    {isSaving ? <Loader2 className="animate-spin h-4 w-4" /> : <Save size={14} />} Save
                                </Button>
                            )}
                        </>
                    )}
                />
            </div>

            <Modal isOpen={isMappingModalOpen} onClose={closeMappingModal} title={editingMapping ? "Edit Transaction Mapping" : "New Transaction Mapping"}>
                <form onSubmit={handleMappingSubmit} className="p-4">
                    <SearchableSelectBox
                        label="Transaction Type"
                        id="transaction_type"
                        name="transaction_type"
                        value={formState.transaction_type}
                        onChange={(e) => setFormState((prev)=>({...prev,[e.target.name]:e.target.value}))}
                        options={transactionTypeOptions}
                        required={true}
                        isDisabled={!!editingMapping && !String(editingMapping.mapping_id).startsWith('default_')} // Disable if editing an existing DB entry
                        placeholder="Select a transaction type"
                        containerClassName="mb-0"
                    />
                    <SearchableSelectBox
                        label="Debit Account"
                        id="debit_account_id"
                        name="debit_account_id"
                        value={formState.debit_account_id}
                        onChange={(e) => setFormState((prev)=>({...prev,[e.target.name]:e.target.value}))}
                        options={glAccountOptions}
                        required={true}
                        placeholder="Select debit account"
                        containerClassName="mb-0"
                    />
                    <SearchableSelectBox
                        label="Credit Account"
                        id="credit_account_id"
                        name="credit_account_id"
                        value={formState.credit_account_id}
                        onChange={(e) => setFormState((prev)=>({...prev,[e.target.name]:e.target.value}))}
                        options={glAccountOptions}
                        required={true}
                        placeholder="Select credit account"
                        containerClassName="mb-0"
                    />
                    <Input
                        label="Description"
                        id="description"
                        name="description"
                        value={formState.description}
                        onChange={handleMappingChange}
                        type="textarea"
                        placeholder="Optional: Brief description for this mapping"
                    />
                    <Checkbox
                        label="Is Active"
                        id="is_active"
                        name="is_active"
                        checked={formState.is_active}
                        onChange={handleMappingChange}
                    />
                    <div className="flex justify-end space-x-2 mt-6">
                        <Button type="button" variant="secondary" onClick={closeMappingModal} disabled={isSaving}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={isSaving}>
                            {isSaving ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                            {editingMapping ? "Update Mapping" : "Add Mapping"}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* New Modal for Adding Transaction Type */}
            <Modal isOpen={isAddTransactionTypeModalOpen} onClose={closeAddTransactionTypeModal} title="Add New Transaction Type">
                <form onSubmit={handleAddTransactionTypeSubmit} className="p-4">
                    <Input
                        label="Transaction Type (Code)"
                        id="transaction_type"
                        name="transaction_type"
                        value={newTransactionTypeForm.transaction_type}
                        onChange={handleNewTransactionTypeChange}
                        placeholder="e.g., SALES_DISCOUNT_CASH"
                        required
                        className="mb-4"
                    />
                    <Input
                        label="Label (Human Readable)"
                        id="label"
                        name="label"
                        value={newTransactionTypeForm.label}
                        onChange={handleNewTransactionTypeChange}
                        placeholder="e.g., Sales Discount Cash"
                        required
                        className="mb-4"
                    />
                    <div className="flex justify-end space-x-2 mt-6">
                        <Button type="button" variant="secondary" onClick={closeAddTransactionTypeModal} disabled={isAddingTransactionType}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={isAddingTransactionType}>
                            {isAddingTransactionType ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                            Add Transaction Type
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default TransactionMappingManager;
